import React, { useState, useEffect, forwardRef, useRef } from 'react';
import { UserJourney } from '@/types/journey';
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { CheckCircle2, User, X } from "lucide-react";
import { ImageUpload, getInitials } from './profile/ImageUpload';
import { useIsMobile } from '@/hooks/use-mobile';

interface UserProfileProps {
  userName: string;
  userRole: string;
  userPhoto: string | null;
  onUpdate: (update: Partial<UserJourney>) => void;
  isComplete?: boolean;
  isMobile?: boolean;
}

// Component for editing user name with cancel/save buttons
const EditableUserName = forwardRef<HTMLDivElement, {
  value: string;
  onChange: (value: string) => void;
  isMobile: boolean;
}>(({ value, onChange, isMobile }, ref) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tempValue, setTempValue] = useState(value);
  
  useEffect(() => {
    setTempValue(value);
  }, [value]);
  
  const handleCancel = () => {
    setTempValue(value);
    setIsEditing(false);
  };
  
  const handleSave = () => {
    onChange(tempValue);
    setIsEditing(false);
  };
  
  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only process if we're in edit mode
      if (!isEditing) return;
      
      // Command+S (Mac) or Ctrl+S (Windows)
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault(); // Prevent browser's save dialog
        handleSave();
      }
      
      // Escape key to cancel
      if (e.key === 'Escape') {
        e.preventDefault();
        handleCancel();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isEditing, tempValue, value, onChange]);
  
  if (isEditing) {
    return (
      <div className="relative" ref={ref}>
        <Input 
          placeholder="Click to add user name (e.g., Sarah Johnson)" 
          value={tempValue} 
          onChange={(e) => setTempValue(e.target.value)}
          className={tempValue ? "" : "text-gray-400 italic"}
          autoFocus
        />
        {tempValue && (
          <button 
            type="button"
            className="absolute right-2 top-2 text-gray-400 hover:text-gray-600"
            onClick={() => setTempValue('')}
            aria-label="Clear"
          >
            <X className="h-4 w-4" />
          </button>
        )}
        <div className="flex justify-end mt-1 space-x-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button 
            size="sm" 
            onClick={handleSave}
            disabled={!tempValue.trim()}
          >
            Save
          </Button>
        </div>
      </div>
    );
  }
  
  return (
    <div 
      onClick={() => setIsEditing(true)} 
      className={`${!value ? 'text-gray-400 italic cursor-text' : 'cursor-pointer'} hover:bg-gray-100 dark:hover:bg-gray-800 p-2 rounded transition-colors`}
      ref={ref}
    >
      <h3 className="text-lg font-semibold">
        {value || 'Click to add user name (e.g., Sarah Johnson)'}
      </h3>
    </div>
  );
});

EditableUserName.displayName = 'EditableUserName';

// Component for editing user role with cancel/save buttons
const EditableUserRole = forwardRef<HTMLDivElement, {
  value: string;
  onChange: (value: string) => void;
  isMobile: boolean;
}>(({ value, onChange, isMobile }, ref) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tempValue, setTempValue] = useState(value);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  
  useEffect(() => {
    setTempValue(value);
  }, [value]);
  
  // Auto-resize textarea based on content
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea || !isEditing) return;

    // Reset height to get the correct scrollHeight
    textarea.style.height = 'auto';
    // Set to scrollHeight to get the full content height
    textarea.style.height = `${textarea.scrollHeight}px`;
  }, [tempValue, isEditing]);
  
  const handleCancel = () => {
    setTempValue(value);
    setIsEditing(false);
  };
  
  const handleSave = () => {
    onChange(tempValue);
    setIsEditing(false);
  };
  
  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only process if we're in edit mode
      if (!isEditing) return;
      
      // Command+S (Mac) or Ctrl+S (Windows)
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault(); // Prevent browser's save dialog
        handleSave();
      }
      
      // Escape key to cancel
      if (e.key === 'Escape') {
        e.preventDefault();
        handleCancel();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isEditing, tempValue, value, onChange]);
  
  if (isEditing) {
    return (
      <div className="relative" ref={ref}>
        <Textarea 
          ref={textareaRef}
          placeholder="Click to add use persona information " 
          value={tempValue} 
          onChange={(e) => setTempValue(e.target.value)}
          className={`w-full min-h-[100px] whitespace-pre-wrap overflow-hidden transition-all ${tempValue ? "" : "text-gray-400 italic"}`}
          style={{ resize: 'none' }}
          autoFocus
        />
        {tempValue && (
          <button 
            type="button"
            className="absolute right-2 top-2 text-gray-400 hover:text-gray-600"
            onClick={() => setTempValue('')}
            aria-label="Clear"
          >
            <X className="h-4 w-4" />
          </button>
        )}
        <div className="flex justify-end mt-2 space-x-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button 
            size="sm" 
            onClick={handleSave}
            disabled={!tempValue.trim()}
          >
            Save
          </Button>
        </div>
      </div>
    );
  }
  
  return (
    <div 
      onClick={() => setIsEditing(true)} 
      className={`${!value ? 'text-gray-400 italic cursor-text' : 'cursor-pointer'} hover:bg-gray-100 dark:hover:bg-gray-800 p-2 rounded transition-colors whitespace-pre-wrap overflow-wrap-break-word`}
      ref={ref}
    >
      <p className="text-sm">
        {value || 'Click to add user role (e.g., Marketing Manager)'}
      </p>
    </div>
  );
});

EditableUserRole.displayName = 'EditableUserRole';

const UserProfile: React.FC<UserProfileProps> = ({ 
  userName, 
  userRole, 
  userPhoto, 
  onUpdate,
  isComplete = false,
  isMobile = false
}) => {
  const _isMobile = useIsMobile() || isMobile;
  
  const handleUpdateName = (newName: string) => {
    onUpdate({ userName: newName });
  };
  
  const handleUpdateRole = (newRole: string) => {
    onUpdate({ userRole: newRole });
  };
  
  const handlePhotoUpdate = (imageData: string | null) => {
    onUpdate({ userPhoto: imageData });
  };
  
  return (
    <TooltipProvider>
      <Tooltip>
        <Card className="shadow-md relative h-full">
          <CardContent className="p-4">
            <div className="flex items-start space-x-5">
              <div className="relative flex-shrink-0">
                <Avatar className="h-24 w-24 rounded-full overflow-hidden bg-gray-100">
                  {userPhoto ? (
                    <AvatarImage 
                      src={userPhoto} 
                      alt={userName} 
                      className="avatar-image object-cover w-full h-full" 
                    />
                  ) : (
                    <AvatarFallback className="bg-gray-100 text-gray-500 flex items-center justify-center">
                      {userName ? getInitials(userName) : <User className="h-12 w-12 text-gray-400" />}
                    </AvatarFallback>
                  )}
                </Avatar>
                
                <ImageUpload onImageProcessed={handlePhotoUpdate} />
              </div>
              
              <div className="flex-1 space-y-3">
                <TooltipTrigger asChild>
                  <EditableUserName 
                    value={userName}
                    onChange={handleUpdateName}
                    isMobile={_isMobile}
                  />
                </TooltipTrigger>
                
                <TooltipTrigger asChild>
                  <EditableUserRole
                    value={userRole}
                    onChange={handleUpdateRole}
                    isMobile={_isMobile}
                  />
                </TooltipTrigger>
                
                {!userName && !userRole && (
                  <p className="text-xs text-muted-foreground mt-2">
                    Click to add user profile details
                  </p>
                )}
              </div>
            </div>
            
            <CheckCircle2 
              className={`h-4 w-4 absolute top-4 right-4 ${isComplete ? 'text-emerald-500' : 'text-gray-300'}`} 
              aria-label={isComplete ? "Profile complete" : "Profile incomplete"}
            />
          </CardContent>
        </Card>
        <TooltipContent>
          <p>Edit user profile. Create a relatable persona to help team members empathize with the user.</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default UserProfile;
